import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  styled,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getProjectScopes,
  createScope,
  updateScope,
} from "../../../../services/scopeService";
import styles from "./styles";
import AddActivityModal from "./AddActivityModal";
import ActivitiesList from "./ActivitiesList";
import DeleteActivityModal from "./DeleteActivityModal";
import { getProjectActivities } from "../../../../services/scheduleService";
import { IoListOutline, IoGridOutline } from "react-icons/io5";
import ActivitiesKanbanView from "./ActivitiesKanbanView";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "none",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "800",
  borderRadius: "10px",
  padding: ".5vh .7vw",
  letterSpacing: "0",
}));

const Schedule = ({ details, isMobile, passedStyles }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddScopeModalOpen, setIsAddScopeModalOpen] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [isDeleteActivityModalOpen, setIsDeleteActivityModalOpen] =
    useState(false);
  const [view, setView] = useState("list"); // default list

  const handleChangeView = (newView) => {
    setView(newView);
  };
  const activitiesStyles = styles(isMobile);

  useEffect(() => {
    if (details?.id) {
      loadScopes();
    }
  }, [details?.id]);

  const loadScopes = async () => {
    try {
      setLoading(true);
      const response = await getProjectActivities(details.id);
      if (response.success) {
        setActivities(response.data || []);
      } else {
        toast.error("Failed to load activities");
      }
    } catch (error) {
      console.error("Error loading activities:", error);
      toast.error("Failed to load activities. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddScope = () => {
    setEditingScope(null);
    setIsAddScopeModalOpen(true);
  };

  const handleScopeSave = () => {
    setIsAddScopeModalOpen(false);
    setEditingScope(null);
    loadScopes();
  };

  const handleDeleteActivity = (activity) => {
    setActivityToDelete(activity);
    setIsDeleteActivityModalOpen(true);
  };

  const handleDeleteActivitySuccess = () => {
    setActivityToDelete(null);
    loadScopes();
  };

  return (
    <div style={activitiesStyles.container}>
      <Box sx={activitiesStyles.header}>
        <Box>
          <div style={activitiesStyles.title}>Activities</div>
        </Box>
        <div style={{ display: "flex", alignItems: "center", gap: "1vw" }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              backgroundColor: "#f4f6f8",
              borderRadius: "12px",
              padding: "4px",
              width: "fit-content",
            }}
          >
            {/* List View Button */}
            <IconButton
              onClick={() => handleChangeView("list")}
              sx={{
                backgroundColor: view === "list" ? "#4D6BF6" : "transparent",
                color: view === "list" ? "#fff" : "#637381",
                "&:hover": {
                  backgroundColor: view === "list" ? "#4D6BF6" : "#e0e0e0",
                },
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              <IoListOutline size={20} />
            </IconButton>

            <IconButton
              onClick={() => handleChangeView("kanban")}
              sx={{
                backgroundColor: view === "kanban" ? "#4D6BF6" : "transparent",
                color: view === "kanban" ? "#fff" : "#637381",
                "&:hover": {
                  backgroundColor: view === "kanban" ? "#4D6BF6" : "#e0e0e0",
                },
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              <IoGridOutline size={20} />
            </IconButton>
          </Box>{" "}
          {/* <StyledButton variant="contained" onClick={handleAddScope}>
            <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
            <p style={{ fontWeight: "500" }}>Add Activity</p>
          </StyledButton> */}
        </div>
      </Box>

      {loading ? (
        <Box sx={activitiesStyles.loadingContainer}>
          <CircularProgress size={40} />
        </Box>
      ) : activities.length === 0 ? (
        <center style={{ padding: "2vmax" }}>No activities found </center>
      ) : view === "list" ? (
        <ActivitiesList
          data={activities}
          details={details}
          getProjectDetails={passedStyles?.getProjectDetails || loadScopes}
          styles={passedStyles}
          isMobile={isMobile}
          isTablet={isTablet}
          onDeleteActivity={handleDeleteActivity}
        />
      ) : (
        <ActivitiesKanbanView data={activities} details={details} />
      )}

      <AddActivityModal
        open={isAddScopeModalOpen}
        onClose={() => setIsAddScopeModalOpen(false)}
        onSave={handleScopeSave}
        editingScope={editingScope}
        projectId={details?.id}
      />

      <DeleteActivityModal
        open={isDeleteActivityModalOpen}
        onClose={() => {
          setIsDeleteActivityModalOpen(false);
          setActivityToDelete(null);
        }}
        activityData={activityToDelete}
        onSuccess={handleDeleteActivitySuccess}
      />
    </div>
  );
};

export default Schedule;
